import { CameraController } from './ThreeCameraController'
import { CreateAabb, CreatePositionVectorFromLatLon } from './ThreeUtils'
import * as THREE from 'three'
import { Location } from './openapi'

export type Marker = {
  position: Location
  id: number
}

export class MarkerController {
  public markers_: Record<number, THREE.Vector3>
  private markerSprites: THREE.Sprite[] = []
  private scene_: THREE.Scene
  private raycaster_: THREE.Raycaster
  private cameraController_: CameraController
  private removedSprite: THREE.Sprite = new THREE.Sprite()
  private highlightedSprite: THREE.Sprite = new THREE.Sprite()
  private markerSize = 0.04
  private markerDictionary: { spriteId: number; markerNumber: number }[] = []

  public constructor(scene_: THREE.Scene, raycaster_: THREE.Raycaster, cameraController_: CameraController) {
    this.markers_ = {}
    this.scene_ = scene_
    this.raycaster_ = raycaster_
    this.cameraController_ = cameraController_
  }

  public resetMarkers() {
    this.markerDictionary = []
    this.removedSprite = new THREE.Sprite()
    this.highlightedSprite = new THREE.Sprite()
  }

  setMarkerAsHighlighted(id: number, SPRITE_LAYER: number) {
    this.removeHighlightFromCurrentMarker()
    const markerNumber = this.markerDictionary.find(sprite => sprite.spriteId.toString() === id.toString())?.markerNumber
    const sprite = this.markerSprites.find(sprite => sprite.name === id.toString())

    if (sprite !== undefined) {
      this.removedSprite = sprite
      this.scene_.remove(sprite)
    }

    if (sprite !== undefined && markerNumber !== undefined) {
      const spritemat = this.createSpriteMat(markerNumber, true)
      const sprite_ = this.createSprite(
        spritemat,
        new THREE.Vector3(sprite.position.x, sprite.position.z, sprite.position.y),
        this.removedSprite.userData as Marker,
        SPRITE_LAYER
      )

      this.scene_.add(sprite_)
      this.highlightedSprite = sprite_
    }
  }

  removeHighlightFromCurrentMarker() {
    this.scene_.remove(this.highlightedSprite)
    this.scene_.add(this.removedSprite)
  }

  createMarkers(SPRITE_LAYER: number, utmzone: number, markers?: Marker[]) {
    this.markers_ = {}
    this.deleteAllSprites()

    if (markers) {
      // Setup raycaster to ensure hit from top to bottom
      const aabb = CreateAabb(this.scene_)

      this.raycaster_.near = 1
      this.raycaster_.far = aabb.max.y - aabb.min.y + 1

      // Setup sprites for each marker
      markers.forEach((marker, index) => {
        // create sprite-material
        this.markerDictionary.push({ spriteId: marker.id, markerNumber: index })
        const spritemat = this.createSpriteMat(index, false)

        // Find the position relative to the scene from the latlon coordinates
        let position: THREE.Vector3 = CreatePositionVectorFromLatLon(
          marker.position,
          utmzone,
          aabb,
          this.cameraController_.GetOrigin()
        )

        // Use altitude if set explicitly on the marker, otherwise place it 10m above the model.
        position = this.setMarkerAltitude(marker, position, aabb)

        const sprite = this.createSprite(spritemat, position, marker, SPRITE_LAYER)
        this.scene_.add(sprite)
        this.markerSprites.push(sprite)
        this.cameraController_.EnableLayer(SPRITE_LAYER)

        this.markers_[marker.id] = new THREE.Vector3(position.y, position.z, position.y)
      })

      // Restore raycasting to pick sprites far away
      this.raycaster_.near = 0.1
      this.raycaster_.far = 5000
    }
  }

  public hasMarker(markerId: number): boolean {
    return this.markers_[markerId] !== undefined
  }

  public getMarkerYPosition(markerId: number) {
    return this.markers_[markerId].y
  }

  private deleteAllSprites() {
    this.scene_.children.forEach(node => {
      if (node instanceof THREE.Sprite) {
        node.removeFromParent()
      }
    })
  }

  private createSpriteMat = (markerId: number, highlightedMarker: boolean): THREE.SpriteMaterial => {
    let spritemat = new THREE.SpriteMaterial()
    if (markerId.toString()) {
      const sz = 128 * window.devicePixelRatio // was 64
      const drawcanvas = document.createElement('canvas')
      drawcanvas.width = sz
      drawcanvas.height = sz
      const ctx = drawcanvas.getContext('2d')

      if (ctx) {
        spritemat = this.createMarkerSprite(ctx, markerId, drawcanvas, highlightedMarker)
      }
    }
    return spritemat
  }

  private setMarkerAltitude(marker: Marker, position: THREE.Vector3, aabb: THREE.Box3) {
    if (marker.position.altitude === undefined || marker.position.altitude === null) {
      this.raycaster_.layers.set(0)
      this.raycaster_.set(new THREE.Vector3(position.x, aabb.max.y + 1, position.y), new THREE.Vector3(0, -1, 0))
      const intersections = this.raycaster_.intersectObject(this.scene_)

      if (intersections.length > 0) {
        position.z = intersections[0].point.y + 10
      }
    } else {
      position.z = marker.position.altitude
    }
    return position
  }

  private createMarkerSprite(ctx: CanvasRenderingContext2D, index: number, drawcanvas: HTMLCanvasElement, highlight: boolean) {
    const spritemat: THREE.SpriteMaterial = new THREE.SpriteMaterial({ sizeAttenuation: false })
    const sz = 128 * window.devicePixelRatio // was 64

    ctx.clearRect(0, 0, sz, sz)

    const createOuterSquare = () => {
      ctx.lineWidth = 15
      ctx.strokeStyle = highlight ? '#72795A' : 'white'
      ctx.arc(sz / 2, sz / 2, (sz / 2) * radiusMultiplier, 0, 2 * Math.PI)
    }

    const createInnerSquare = () => {
      ctx.beginPath()
      ctx.strokeStyle = highlight ? 'white' : '#72795A'
      ctx.fillStyle = highlight ? 'white' : '#72795A'
      ctx.arc(sz / 2, sz / 2, (sz / 2) * radiusMultiplier, 0, 2 * Math.PI)
      ctx.fill()
    }

    const radiusMultiplier = 0.9

    createInnerSquare()
    createOuterSquare()
    ctx.stroke()

    ctx.fillStyle = highlight ? '#72795A' : 'white'
    ctx.font = `${sz * 0.6}px Arial` // was 0.8
    const metric = ctx.measureText(index.toString())

    ctx.textAlign = 'center'
    // Enumerate drawings from 1 for each period. The marker.id is
    // still stored in marker.userData
    ctx.fillText(
      (index + 1).toString(),
      drawcanvas.width / 2,
      drawcanvas.height - (drawcanvas.height - metric.actualBoundingBoxAscent) / 2
    )

    spritemat.map = new THREE.CanvasTexture(drawcanvas)
    spritemat.map.colorSpace = THREE.SRGBColorSpace
    spritemat.map.needsUpdate = true

    spritemat.opacity = highlight ? 1 : 0.7

    return spritemat
  }

  private createSprite(spritemat: THREE.SpriteMaterial, position: THREE.Vector3, marker: Marker, SPRITE_LAYER: number) {
    const sprite = new THREE.Sprite(spritemat)
    sprite.renderOrder = 1
    sprite.position.set(position.x, position.z, position.y)
    sprite.scale.set(this.markerSize, this.markerSize, this.markerSize)
    sprite.name = marker.id.toString()
    sprite.layers.set(SPRITE_LAYER)
    sprite.userData = marker
    return sprite
  }
}
